import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { BlockEditor } from "@/components/BlockEditor";
import { MediaPickerDialog } from "@/components/MediaPickerDialog";

const EMPTY = {
  title: "", slug: "", subtitle: "", category_id: "", category_ids: [], tag_ids: [],
  author_name: "", status: "draft", scheduled_at: "", cover_image: "", cover_image_alt: "",
  content_blocks: [], seo_title: "", seo_description: "", is_lead: false, is_featured: false,
  is_top: false, is_editors_pick: false, related_article_ids: [],
};

function genKey() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export default function AdminArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [allArticles, setAllArticles] = useState([]);
  const [relatedQuery, setRelatedQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/tags").then(({ data }) => setTags(data));
    api.get("/articles", { params: { status: "all", limit: 200 } }).then(({ data }) => setAllArticles(data.items));
    if (id) {
      api.get(`/articles/by-id/${id}`).then(({ data }) => {
        setForm({
          ...data,
          category_ids: data.category_ids.filter((c) => c !== data.category_id),
          scheduled_at: data.scheduled_at ? data.scheduled_at.slice(0, 16) : "",
          content_blocks: (data.content_blocks || []).map((b) => ({ ...b, key: b.key || genKey() })),
        });
      });
    }
  }, [id]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleExtraCategory = (catId) => {
    if (catId === form.category_id) return;
    update({ category_ids: form.category_ids.includes(catId) ? form.category_ids.filter((c) => c !== catId) : [...form.category_ids, catId] });
  };

  const toggleTag = (tagId) => {
    update({ tag_ids: form.tag_ids.includes(tagId) ? form.tag_ids.filter((t) => t !== tagId) : [...form.tag_ids, tagId] });
  };

  const createTag = async () => {
    if (!newTag.trim()) return;
    const { data } = await api.post("/tags", { name: newTag });
    setTags((t) => [...t, data]);
    update({ tag_ids: [...form.tag_ids, data.id] });
    setNewTag("");
  };

  const toggleRelated = (articleId) => {
    update({ related_article_ids: form.related_article_ids.includes(articleId) ? form.related_article_ids.filter((a) => a !== articleId) : [...form.related_article_ids, articleId] });
  };

  const openPicker = (onSelectFn) => {
    setPickerTarget(() => onSelectFn);
    setPickerOpen(true);
  };

  const submit = (statusOverride) => async (e) => {
    e.preventDefault();
    if (!form.category_id) {
      toast.error("Изберете основна категория");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      status: statusOverride || form.status,
      scheduled_at: form.scheduled_at || null,
      content_blocks: form.content_blocks.map(({ key, ...rest }) => rest),
    };
    try {
      if (id) {
        await api.put(`/articles/${id}`, payload);
      } else {
        const { data } = await api.post("/articles", payload);
        navigate(`/admin/articles/${data.id}/edit`);
      }
      toast.success("Статията е запазена");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredRelated = allArticles.filter((a) => a.id !== id && a.title.toLowerCase().includes(relatedQuery.toLowerCase()));

  return (
    <div data-testid="admin-article-editor-page">
      <h1 className="font-serif text-3xl font-bold text-[#1A1A1A] mb-8">{id ? "Редактиране на статия" : "Нова статия"}</h1>
      <form onSubmit={submit()} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <Input required placeholder="Заглавие" value={form.title} onChange={(e) => update({ title: e.target.value })} data-testid="article-title-input" />
            <Textarea placeholder="Подзаглавие / кратко резюме" value={form.subtitle} onChange={(e) => update({ subtitle: e.target.value })} data-testid="article-subtitle-input" />
            <div className="flex gap-3">
              <Input placeholder="URL slug (по избор)" value={form.slug} onChange={(e) => update({ slug: e.target.value })} data-testid="article-slug-input" />
              <Input placeholder="Автор" value={form.author_name} onChange={(e) => update({ author_name: e.target.value })} data-testid="article-author-input" />
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-semibold text-[#1A1A1A] mb-3">Основно изображение</h3>
            {form.cover_image && <img src={form.cover_image} alt="" className="w-full max-w-md aspect-video object-cover mb-3" />}
            <Button type="button" variant="outline" onClick={() => openPicker((url) => update({ cover_image: url }))} data-testid="article-pick-cover-btn">
              Изберете основно изображение
            </Button>
            <Input className="mt-3" placeholder="Alt текст на основното изображение" value={form.cover_image_alt} onChange={(e) => update({ cover_image_alt: e.target.value })} data-testid="article-cover-alt-input" />
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-semibold text-[#1A1A1A] mb-3">Съдържание</h3>
            <BlockEditor blocks={form.content_blocks} onChange={(blocks) => update({ content_blocks: blocks })} onPickImage={openPicker} />
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6 space-y-3">
            <h3 className="font-semibold text-[#1A1A1A]">SEO</h3>
            <Input placeholder="SEO title" value={form.seo_title} onChange={(e) => update({ seo_title: e.target.value })} data-testid="article-seo-title-input" />
            <Textarea placeholder="SEO meta description" value={form.seo_description} onChange={(e) => update({ seo_description: e.target.value })} data-testid="article-seo-description-input" />
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-semibold text-[#1A1A1A] mb-3">Свързани статии (ръчен избор)</h3>
            <Input placeholder="Търси статия..." value={relatedQuery} onChange={(e) => setRelatedQuery(e.target.value)} className="mb-3" data-testid="article-related-search-input" />
            <div className="max-h-56 overflow-y-auto space-y-1">
              {filteredRelated.slice(0, 30).map((a) => (
                <label key={a.id} className="flex items-center gap-2 text-sm py-1">
                  <Checkbox checked={form.related_article_ids.includes(a.id)} onCheckedChange={() => toggleRelated(a.id)} data-testid={`article-related-checkbox-${a.id}`} />
                  {a.title}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#E2E8F0] p-6 space-y-4">
            <h3 className="font-semibold text-[#1A1A1A]">Публикуване</h3>
            <Select value={form.status} onValueChange={(v) => update({ status: v })}>
              <SelectTrigger data-testid="article-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Чернова</SelectItem>
                <SelectItem value="published">Публикувана</SelectItem>
                <SelectItem value="unpublished">Свалена</SelectItem>
                <SelectItem value="scheduled">Насрочена</SelectItem>
              </SelectContent>
            </Select>
            {form.status === "scheduled" && (
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => update({ scheduled_at: e.target.value })} data-testid="article-scheduled-at-input" />
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={submit("draft")} variant="outline" className="flex-1" disabled={saving} data-testid="article-save-draft-btn">Запази чернова</Button>
              <Button type="submit" className="flex-1 bg-[#A6192E] hover:bg-[#8C1123]" disabled={saving} data-testid="article-save-btn">Запази</Button>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6 space-y-3">
            <h3 className="font-semibold text-[#1A1A1A]">Категории</h3>
            <Select value={form.category_id} onValueChange={(v) => v && update({ category_id: v })}>
              <SelectTrigger data-testid="article-main-category-select"><SelectValue placeholder="Основна категория" /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {categories.filter((c) => c.id !== form.category_id).map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm py-0.5">
                  <Checkbox checked={form.category_ids.includes(c.id)} onCheckedChange={() => toggleExtraCategory(c.id)} data-testid={`article-extra-category-${c.slug}`} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6 space-y-3">
            <h3 className="font-semibold text-[#1A1A1A]">Тагове</h3>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  className={`text-xs px-2.5 py-1 border ${form.tag_ids.includes(t.id) ? "bg-[#A6192E] text-white border-[#A6192E]" : "border-[#E2E8F0] text-[#2D3748]"}`}
                  data-testid={`article-tag-${t.slug}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Нов таг" value={newTag} onChange={(e) => setNewTag(e.target.value)} data-testid="article-new-tag-input" />
              <Button type="button" variant="outline" onClick={createTag} data-testid="article-new-tag-btn">+</Button>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6 space-y-2">
            <h3 className="font-semibold text-[#1A1A1A] mb-2">Акценти</h3>
            {[
              ["is_lead", "Водеща"], ["is_featured", "Акцент"], ["is_top", "Най-важно"], ["is_editors_pick", "Препоръчано от редакцията"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-sm py-0.5">
                <Checkbox checked={form[key]} onCheckedChange={(v) => update({ [key]: v === true })} data-testid={`article-flag-${key}`} />
                {label}
              </label>
            ))}
          </div>
        </div>
      </form>

      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(url) => pickerTarget && pickerTarget(url)} />
    </div>
  );
}
